<template>
  <div class="d-flex" style="min-height: 45vw;">
    <div class="content p-4 w-100">

      <!-- HEADER -->
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="card-title mt-0">
          {{ i18n.global.t('pages.firmwareBulkUpdate.formTitle') }}
        </h4>
      </div>

      <!-- MAIN CONTENT -->
      <v-row class="mt-4">
        <!-- LEFT -->
        <v-col>
          <TargetDevice
            v-model="targetDevice"
            :errorMessages="targetErrorMessages"
          />

          <ApplyDate
            v-model="applyDate"
            :errorMessages="errorMessages['activation_date']"
          />

          <UpdateVersion
            v-model="updateVersion"
            :initialItemId="updateVersionId"
            :errorMessages="errorMessages['firmware_id']"
          />

          <VersionNote
            :note="updateVersion?.memo"
          />

          <v-row v-if="updateResultErrorMessages">
            <v-col class="fw-bold">
              <v-textarea
                label="error messages"
                class="text-red mt-4"
                v-model="updateResultErrorMessages"
                :clearable="false"
              ></v-textarea>
            </v-col>
          </v-row>

        </v-col>

        <v-divider inset vertical></v-divider>

        <!-- RIGHT -->
        <v-col>
          <RemoveDeviceSearch
            v-model="removeDeviceIds"
            :removeDevices="removeDevices"
            :errorMessages="removeDeviceErrorMessages"
          />
        </v-col>
      </v-row>

      <!-- FOOTER -->
      <v-row>
        <v-col class="d-flex justify-content-center align-items-center">
          <router-link
              :to="{name: isLoading ? null : 'FirmwareBulkUpdateList'}"
          >
            <v-btn
              class="reset-btn mr-2"
              :disabled="isLoading"
            >
              {{ i18n.global.t('pages.firmwareBulkUpdate.btn.cancel') }}
            </v-btn>
          </router-link>
          <v-btn
            v-if="!isEdit"
            class="exec-btn ml-2"
            :loading="isLoading"
            @click="submitBulkUpdate"
          >
            {{ i18n.global.t('pages.firmwareBulkUpdate.btn.apply') }}
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import Api from '@/services/api/ApiServiceFabrick';
import i18n from '@/lang/i18n';
import { useRoute, useRouter } from 'vue-router';
import TargetDevice from '@/components/Firmware/BulkUpdate/TargetDevice.vue';
import ApplyDate from '@/components/Firmware/BulkUpdate/ApplyDate.vue';
import UpdateVersion from '@/components/Firmware/BulkUpdate/UpdateVersion.vue';
import VersionNote from '@/components/Firmware/BulkUpdate/VersionNote.vue';
import RemoveDeviceSearch from '@/components/Firmware/BulkUpdate/RemoveDeviceSearch.vue';
import FirmwareVersionsType from '@/types/FirmwareVersionsType';
import FirmwareTargetDeviceType from '@/types/FirmwareTargetDeviceType';
import { formatDateTimeWithSeconds } from '@/services/functions';
import FirmwareBulkUpdateExclusionType from '@/types/FirmwareBulkUpdateExclusionType';
import createFirmwareBulkUpdateSettingRequest from '@/types/requests/createFirmwareBulkUpdateSettingRequest';
import getDevice from '@/types/responses/getDevice';

const now = new Date()
now.setHours(0, 0, 0, 0)
const route = useRoute()
const router = useRouter();

const props = defineProps<{
  showFlash: (message: string, type: string, fadeout: boolean) => void
}>()

const targetDevice = ref<FirmwareTargetDeviceType>({
  category: '',
  target: undefined
})
const applyDate = ref<Date>(now)
const updateVersion = ref<FirmwareVersionsType>()
const updateVersionId = ref<number | undefined>(undefined)
const removeDeviceIds = ref<number[]>([])
const removeDevices = ref<getDevice[]>([])
const errorMessages = ref<{[key: string]: string[]}>({})
const targetErrorMessages = ref<{[key: string]: string[]}>({})
const removeDeviceErrorMessages = ref<string[]>([])
const updateResultErrorMessages = ref<string>('')
const isLoading = ref(false)
const isEdit = computed(() => !!route.params.id)

onMounted(() => {
  if (isEdit.value) {
    fetchBulkUpdateData()
  }
})

function fetchBulkUpdateData() {
  isLoading.value = true
  resetErrorMessages()

  const settingId = Number(route.params.id);
  Api().getFirmwareBulkUpdateSetting(settingId).then((data) => {
    targetDevice.value = {
      category: data.target_category,
      target: {id: data.target_id} as FirmwareTargetDeviceType['target']
    }
    applyDate.value = new Date(data.activation_date)
    updateVersionId.value = data.firmware_id
    removeDeviceIds.value = data.firmware_bulk_update_exclusions.map(
      (item: FirmwareBulkUpdateExclusionType) => item.device_id
    )
    removeDevices.value = data.firmware_bulk_update_exclusions.map(
      (item: FirmwareBulkUpdateExclusionType) => item.device
    )
    isLoading.value = false
  }).catch((error) => {
    isLoading.value = false
  })
}

function submitBulkUpdate() {
  isLoading.value = true
  resetErrorMessages()

  const isValid = validation()
  if (!isValid) {
    isLoading.value = false
    return
  }

  const params = {
    firmware_id: updateVersion.value?.id,
    activation_date: formatDateTimeWithSeconds(applyDate.value),
    target_category: targetDevice.value.category,
    target_id: targetDevice.value?.target?.id,
    remove_device_ids: removeDeviceIds.value
  } as createFirmwareBulkUpdateSettingRequest

  Api().createFirmwareBulkUpdateSetting(params).then(() => {
    isLoading.value = false
    props.showFlash(i18n.global.t('pages.common.messages.success'), 'success', true);
    router.push({name: 'FirmwareBulkUpdateList'});
  }).catch((error) => {
    const res = error.response
    switch (res.status) {
      case 400:
        updateResultErrorMessages.value = res.data.message
        break;
      case 422:
        errorMessages.value = res.data.errors
        targetErrorMessages.value['target_category'] = res.data.errors['target_category'] || []
        targetErrorMessages.value['target_id'] = res.data.errors['target_id'] || []
        removeDeviceErrorMessages.value = Object.entries(errorMessages.value)
          .filter(([key]) => key.includes('remove_device_ids'))
          .flatMap(([key, value]) => value);
    }
    isLoading.value = false
  })
}

function resetErrorMessages() {
  errorMessages.value = {}
  targetErrorMessages.value = {}
  updateResultErrorMessages.value = ''
}

function validation() {
  let isValid = true
  if (!targetDevice.value['category']) {
    targetErrorMessages.value['target_category'] = [i18n.global.t('pages.firmwareBulkUpdate.messages.validSelect')]
    isValid = false
  }
  if (targetDevice.value['category'] && !targetDevice.value['target']) {
    targetErrorMessages.value['target_id'] = [i18n.global.t('pages.firmwareBulkUpdate.messages.validSelect')]
    isValid = false
  }
  if (!updateVersion.value) {
    errorMessages.value['firmware_id'] = [i18n.global.t('pages.firmwareBulkUpdate.messages.validSelect')]
    isValid = false
  }
  if (!applyDate.value) {
    errorMessages.value['activation_date'] = [i18n.global.t('pages.firmwareBulkUpdate.messages.validSelect')]
    isValid = false
  }
  return isValid
}

</script>

<style scoped>
.content {
  min-width: 58vw;
}
</style>
